/**
 * Copyright 2014 Mozilla Foundation
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __extends } from "tslib";
import { AVM1ArrayNative } from '../natives';
import { alCoerceString, alNewObject, alToBoolean, alToNumber, alToString } from '../runtime';
import { wrapAVM1NativeClass } from './AVM1Utils';
import { TextField, TextFormat } from '@awayjs/scene';
import { AVM1Object } from '../runtime/AVM1Object';
import { notImplemented } from '@awayfl/swf-loader';
var AVM1TextFormat = /** @class */ (function (_super) {
    __extends(AVM1TextFormat, _super);
    function AVM1TextFormat() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    AVM1TextFormat.createAVM1Class = function (context) {
        var members = ['align#', 'blockIndent#', 'bold#', 'bullet#', 'color#', 'font#',
            'getTextExtent', 'indent#', 'italic#', 'kerning#', 'leading#',
            'leftMargin#', 'letterSpacing#', 'rightMargin#', 'size#', 'tabStops#',
            'target#', 'adaptee#', 'underline#', 'url#'];
        var wrapped = wrapAVM1NativeClass(context, true, AVM1TextFormat, [], members, null, AVM1TextFormat.prototype.avm1Constructor);
        var proto = wrapped.alGetPrototypeProperty();
        var p = null;
        members.forEach(function (x) {
            if (x[x.length - 1] === '#') {
                x = x.slice(0, -1);
            }
            p = proto.alGetOwnProperty(x);
            p.flags &= ~1 /* DONT_ENUM */;
            proto.alSetOwnProperty(x, p);
        });
        return wrapped;
    };
    AVM1TextFormat.createFromNative = function (context, awayObject) {
        var TextFormat = context.globals.TextFormat;
        var obj = new AVM1TextFormat(context);
        obj.alPrototype = TextFormat.alGetPrototypeProperty();
        obj.adaptee = awayObject;
        return obj;
    };
    AVM1TextFormat.prototype.alPut = function (p, v) {
        if (p == 'font') {
            this.setFont(v);
        }
        _super.prototype.alPut.call(this, p, v);
    };
    AVM1TextFormat.prototype.avm1Constructor = function (font, size, color, bold, italic, underline, url, target, align, leftMargin, rightMargin, indent, leading) {
        var context = this.context;
        font = (font == null) ? null : alToString(context, font);
        size = (size == null) ? null : alToNumber(context, size);
        color = (color == null) ? null : alToNumber(context, color);
        bold = (bold == null) ? null : alToBoolean(context, bold);
        italic = (italic == null) ? null : alToBoolean(context, italic);
        underline = (underline == null) ? null : alToBoolean(context, underline);
        url = (url == null) ? null : alToString(context, url);
        target = (target == null) ? null : alToString(context, target);
        align = (align == null) ? null : alToString(context, align);
        leftMargin = (leftMargin == null) ? null : alToNumber(context, leftMargin);
        rightMargin = (rightMargin == null) ? null : alToNumber(context, rightMargin);
        indent = (indent == null) ? null : alToNumber(context, indent);
        leading = (leading == null) ? null : alToNumber(context, leading);
        var awayObject = new TextFormat(font, size, color, bold, italic, underline, url, target, align, leftMargin, rightMargin, indent, leading);
        this.adaptee = awayObject;
    };
    AVM1TextFormat.alInitStatic = function (context) {
        // See _measureTextField usage in the getTextExtent() below.
        var measureTextField = new TextField();
        measureTextField.multiline = true;
        this._measureTextField = measureTextField;
    };
    AVM1TextFormat.prototype.getAdaptee = function () {
        return this.adaptee.id;
    };
    AVM1TextFormat.prototype.setAdaptee = function (value) { };
    AVM1TextFormat.prototype.getAlign = function () {
        return this.adaptee.align;
    };
    AVM1TextFormat.prototype.setAlign = function (value) {
        var alignAsString = alToString(this.context, value);
        if (alignAsString == '') {
            alignAsString = 'center';
        }
        this.adaptee.align = alignAsString;
    };
    AVM1TextFormat.prototype.getBlockIndent = function () {
        notImplemented('AVM1TextFormat.getBlockIndent');
        return this.adaptee.blockIndent;
    };
    AVM1TextFormat.prototype.setBlockIndent = function (value) {
        notImplemented('AVM1TextFormat.setBlockIndent');
        this.adaptee.blockIndent = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getBold = function () {
        return this.adaptee.bold;
    };
    AVM1TextFormat.prototype.setBold = function (value) {
        this.adaptee.bold = alToBoolean(this.context, value);
    };
    AVM1TextFormat.prototype.getBullet = function () {
        notImplemented('AVM1TextFormat.getBullet');
        return this.adaptee.bullet;
    };
    AVM1TextFormat.prototype.setBullet = function (value) {
        notImplemented('AVM1TextFormat.setBullet');
        this.adaptee.bullet = alToBoolean(this.context, value);
    };
    AVM1TextFormat.prototype.getColor = function () {
        return this.adaptee.color;
    };
    AVM1TextFormat.prototype.setColor = function (value) {
        this.adaptee.color = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getFont = function () {
        return this.adaptee.font_name;
    };
    AVM1TextFormat.prototype.setFont = function (value) {
        // in awayjs "font" is a AwayJS-Font, but if we pass it a string it will stil get the font
        this.adaptee.font = alToString(this.context, value);
    };
    AVM1TextFormat.prototype.getIndent = function () {
        return this.adaptee.indent;
    };
    AVM1TextFormat.prototype.setIndent = function (value) {
        this.adaptee.indent = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getItalic = function () {
        return this.adaptee.italic;
    };
    AVM1TextFormat.prototype.setItalic = function (value) {
        this.adaptee.italic = alToBoolean(this.context, value);
    };
    AVM1TextFormat.prototype.getKerning = function () {
        notImplemented('AVM1TextFormat.getKerning');
        return this.adaptee.kerning;
    };
    AVM1TextFormat.prototype.setKerning = function (value) {
        notImplemented('AVM1TextFormat.setKerning');
        this.adaptee.kerning = alToBoolean(this.context, value);
    };
    AVM1TextFormat.prototype.getLeading = function () {
        return this.adaptee.leading;
    };
    AVM1TextFormat.prototype.setLeading = function (value) {
        this.adaptee.leading = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getLeftMargin = function () {
        return this.adaptee.leftMargin;
    };
    AVM1TextFormat.prototype.setLeftMargin = function (value) {
        this.adaptee.leftMargin = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getLetterSpacing = function () {
        return this.adaptee.letterSpacing;
    };
    AVM1TextFormat.prototype.setLetterSpacing = function (value) {
        this.adaptee.letterSpacing = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getRightMargin = function () {
        return this.adaptee.rightMargin;
    };
    AVM1TextFormat.prototype.setRightMargin = function (value) {
        this.adaptee.rightMargin = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getSize = function () {
        return this.adaptee.size;
    };
    AVM1TextFormat.prototype.setSize = function (value) {
        this.adaptee.size = alToNumber(this.context, value);
    };
    AVM1TextFormat.prototype.getTabStops = function () {
        notImplemented('AVM1TextFormat.getTabStops');
        return new AVM1ArrayNative(this.context, this.adaptee.tabStops);
    };
    AVM1TextFormat.prototype.setTabStops = function (value) {
        var _this = this;
        this.adaptee.tabStops = AVM1ArrayNative.mapToJSArray(value, function (item) { return alToNumber(_this.context, item); });
    };
    AVM1TextFormat.prototype.getTarget = function () {
        notImplemented('AVM1TextFormat.getTarget');
        // return this.adaptee.target;
        return '';
    };
    AVM1TextFormat.prototype.setTarget = function (value) {
        notImplemented('AVM1TextFormat.setTarget');
        // this.adaptee.target = alToString(this.context, value);
    };
    AVM1TextFormat.prototype.getTextExtent = function (text, width) {
        notImplemented('AVM1TextFormat.getTextExtent');
        text = alCoerceString(this.context, text);
        width = +width;
        var staticState = this.context.getStaticState(AVM1TextFormat);
        var measureTextField = staticState._measureTextField;
        if (!isNaN(width) && width > 0) {
            measureTextField.width = width + 4;
            measureTextField.wordWrap = true;
        }
        else {
            measureTextField.wordWrap = false;
        }
        measureTextField.textFormat = this.adaptee;
        measureTextField.text = text;
        var result = alNewObject(this.context);
        var textWidth = measureTextField.textWidth;
        var textHeight = measureTextField.textHeight;
        result.alPut('width', textWidth);
        result.alPut('height', textHeight);
        result.alPut('textFieldWidth', textWidth + 4);
        result.alPut('textFieldHeight', textHeight + 4);
        //const metrics = measureTextField.getLineMetrics(0);
        // todo: this causes compile errors:
        //result.alPut('ascent', metrics.axGetPublicProperty('ascent'));
        //result.alPut('descent',	metrics.axGetPublicProperty('descent'));
        return result;
    };
    AVM1TextFormat.prototype.getUnderline = function () {
        notImplemented('AVM1TextFormat.getUnderline');
        return this.adaptee.underline;
    };
    AVM1TextFormat.prototype.setUnderline = function (value) {
        notImplemented('AVM1TextFormat.setUnderline');
        this.adaptee.underline = alToBoolean(this.context, value);
    };
    AVM1TextFormat.prototype.getUrl = function () {
        notImplemented('AVM1TextFormat.getUrl');
        return this.adaptee.url;
    };
    AVM1TextFormat.prototype.setUrl = function (value) {
        notImplemented('AVM1TextFormat.setUrl');
        this.adaptee.url = alToString(this.context, value);
    };
    return AVM1TextFormat;
}(AVM1Object));
export { AVM1TextFormat };
